import {useCallback, useEffect, useRef} from "react";
import {ColorUtils} from "../../../../../utils/ColorUtils";
import {Constants} from "../../../../modules/space/spaceeditor/logic3d/Constants";
import type {PointDouble} from "../../../../../generated/api/base";
import {THREEUtils} from "../../../../../utils/THREEUtils";
import type {ThemeType} from "../../../../ThemeType";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";

interface INavigationCameraBoxProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly domWidth: number;
	readonly domHeight: number;
	readonly bgWidth: number;
	readonly bgHeight: number;
	readonly themeType: ThemeType;
}

export const NavigationCameraBoxV5 = (props: INavigationCameraBoxProps) => {
	const {spaceViewRenderer, domWidth, domHeight, bgWidth, bgHeight, themeType} = props;
	const _canvasRef = useRef<HTMLCanvasElement>(null);

	const update = useCallback(() => {
		const canvas = _canvasRef.current;

		if (canvas) {
			const _ctx = canvas.getContext("2d");

			_ctx.clearRect(0, 0, domWidth, domHeight);
			_ctx.beginPath();

			_ctx.strokeStyle = `#${Constants.COLORS.PICTON_BLUE}`;
			_ctx.fillStyle = ColorUtils.hex2rgb(_ctx.strokeStyle, 0.1) as string;

			// with 0 camerarotation in mind
			const arrayOfNDCCoords = [
				{x: 1, y: 1},
				{x: -1, y: 1},
				{x: -1, y: -1},
				{x: 1, y: -1},
			];

			const arrayOfDomCoords: PointDouble[] = [];
			const spaceSize = spaceViewRenderer.spaceSize;

			if (spaceSize) {
				const bgElement = {
					offsetWidth: bgWidth,
					offsetHeight: bgHeight,
				};

				const offset = {
					x: (domWidth - bgWidth) / 2,
					y: (domHeight - bgHeight) / 2,
				};

				const spaceOffset = spaceViewRenderer.spaceOffset;

				for (const coord of arrayOfNDCCoords) {
					const worldZ = spaceViewRenderer.spaceOffset.z;
					let worldCoord = THREEUtils.NDCtoWorldCoordinates(coord.x, coord.y, worldZ, spaceViewRenderer.activeCamera);

					if (!worldCoord) {
						worldCoord = THREEUtils.NDCtoWorldCoordinates(coord.x, 0, worldZ, spaceViewRenderer.activeCamera);
					}
					if (!worldCoord) {
						return;
					}
					// Bottom left corner is spaceOffset.xy by default (which is (0, 0) by default)
					arrayOfDomCoords.push(
						THREEUtils.worldCoordinatesToDomCoordinates(worldCoord.x - spaceOffset.x, worldCoord.y - spaceOffset.y, worldZ, bgElement, spaceSize),
					);
					arrayOfDomCoords[arrayOfDomCoords.length - 1].x += offset.x;
					arrayOfDomCoords[arrayOfDomCoords.length - 1].y += offset.y;
				}

				_ctx.moveTo(arrayOfDomCoords[0].x, arrayOfDomCoords[0].y);
				for (let i = 1; i < arrayOfDomCoords.length; ++i) {
					_ctx.lineTo(arrayOfDomCoords[i].x, arrayOfDomCoords[i].y);
				}
			}
			_ctx.closePath();
			_ctx.stroke();
			_ctx.fill();
		}
	}, [domWidth, domHeight, bgWidth, bgHeight, spaceViewRenderer]);

	const {cameraPropsChange} = spaceViewRenderer.toolManager.cameraControls.signals;

	useEffect(() => {
		cameraPropsChange.add(update);

		return () => {
			cameraPropsChange.remove(update);
		};
	}, [update, cameraPropsChange]);

	const style = {
		filter: themeType === "light" ? "none" : "invert(1)",
	};

	return (
		<canvas
			ref={_canvasRef}
			style={style}
			width={domWidth}
			height={domHeight}
		></canvas>
	);
};
